<script setup lang="ts">
// 1. Imports
import { onMounted, h, ref, VNode, reactive, watch, computed } from 'vue';
import { useAdminOrganizationsStore } from '@/stores/admin_organizations';
import { useOrganizationStore } from '@/stores/organizationStore';
import {
  NSpace,
  NButton,
  NDropdown,
  NIcon,
  NPopconfirm,
  NPopover,
  type DataTableColumns,
  type DropdownOption,
  type FormInst,
  type FormRules,
} from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import OrgModelSettingsFormItems from '@/components/admin/OrgModelSettingsFormItems.vue';
import type { SortOrder } from 'naive-ui/es/data-table/src/interface';
import {
  AccessPermission,
  OrganisationInDb,
  OrganisationCreate,
  OrgFeature,
  type ContractManagementMissingExtractions,
  type OrganisationMemberAccess,
  OrganisationModelSettingsCreate,
  OrganisationUpdate,
  SearchQuery,
  BillingPeriod,
  DemoDataArea,
  getDemoDataStatus,
} from '@/api';
import { DEMO_AREA_OPTIONS, useDemoSeedStore } from '@/stores/demoSeedStore';
import {
  IconAdd,
  IconAgent,
  IconDelete,
  IconEdit,
  IconExperiment,
  IconMore,
  IconRefresh,
  IconSwap,
} from '@/components/design/icons';
import { isEnumValue } from '@/utils/enumUtils';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaCheckbox from '@/components/design/base/GaiaCheckbox.vue';
import GaiaCheckboxGroup from '@/components/design/base/GaiaCheckboxGroup.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPopconfirm from '@/components/design/composite/GaiaPopconfirm.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import GaiaSelectRow from '@/components/design/composite/GaiaSelectRow.vue';
import { ORG_WIDE_ACCESS_OPTIONS } from '@/utils/accessPermission';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// 2. Store access
const store = useAdminOrganizationsStore();
const orgStore = useOrganizationStore();
/** Shared by the create and edit organisation forms. */
const CREDITS_PERIOD_OPTIONS = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Annual', value: 'annual' },
];

const message = useGaiaToast();

// 3. Local state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const orgUsage = ref<Record<string, number | null | undefined>>({});
const refreshingUsage = ref<boolean>(false);
const searchQuery = ref<string>('');
const sortKey = ref<string>('name');
const sortOrder = ref<'asc' | 'desc'>('asc');

// Create form state
const showCreateModal = ref<boolean>(false);
const createNameError = ref<string | undefined>(undefined);
const createFormModel = reactive({
  name: '',
  description: null as string | null,
  allowed_insights_credits: 100,
  allowed_insights_credits_period: 'monthly',
  billing_start_date: null as string | null,
});
/** The forms keep one switch per feature; the API takes the set of enabled ones. */
type FeatureSwitches = Record<OrgFeature, boolean>;

function featureSwitches(features: OrgFeature[] = []): FeatureSwitches {
  return Object.fromEntries(
    Object.values(OrgFeature).map(feature => [feature, features.includes(feature)])
  ) as FeatureSwitches;
}

function enabledFeatures(switches: FeatureSwitches): OrgFeature[] {
  return Object.values(OrgFeature).filter(feature => switches[feature]);
}

const createFormFeatureFlags = reactive<FeatureSwitches>(featureSwitches());
const createFormModelSettings = ref<OrganisationModelSettingsCreate>({});
const createFormMemberAccess = reactive<Required<OrganisationMemberAccess>>({
  all_contractual_parties_access: AccessPermission._0,
  all_contracts_access: AccessPermission._0,
});
const updateFormFeatureFlags = reactive<FeatureSwitches>(featureSwitches());
/** Back-fill counts for the org being edited; only fetched while it has contract management. */
const missingExtractions = ref<ContractManagementMissingExtractions | null>(null);
const runningExtractions = ref<boolean>(false);

// Edit form state
const showEditModal = ref<boolean>(false);
const editFormRef = ref<FormInst | null>(null);
const editFormModel = reactive({
  name: '',
  description: null as string | null,
  allowed_insights_credits: 0,
  allowed_insights_credits_period: 'monthly',
  billing_start_date: null as string | null,
});
const currentEditId = ref<string>('');

// Model settings form state
const showModelSettingsModal = ref<boolean>(false);
const modelSettingsForm = ref<OrganisationModelSettingsCreate>({});
const currentModelSettingsOrg = ref<OrganisationInDb | null>(null);
const modelSettingsLoading = ref<boolean>(false);

// Demo data state — the modal targets the clicked row's organisation via the
// admin by-uid endpoints; the run itself (progress, terminal states) lives in
// the demo seed store and is rendered by the global DemoSeedPanel.
const demoSeedStore = useDemoSeedStore();
const showDemoDataModal = ref<boolean>(false);
const demoDataOrg = ref<OrganisationInDb | null>(null);
const selectedDemoAreas = ref<DemoDataArea[]>([]);
const seededDemoAreas = ref<Partial<Record<DemoDataArea, boolean>>>({});
const seedDemoLoading = ref<boolean>(false);
const removeDemoLoading = ref<boolean>(false);

// Delete confirmation state
const showDeleteModal = ref<boolean>(false);
const orgPendingDeletion = ref<OrganisationInDb | null>(null);
const deleteConfirmationInput = ref<string>('');
const deleteNameMatches = computed<boolean>(
  () => deleteConfirmationInput.value === orgPendingDeletion.value?.name
);

const formRules: FormRules = {
  name: [
    {
      required: true,
      message: 'Organization name is required',
      trigger: ['blur', 'input'],
    },
  ],
};

function wrapWithActionPopover(trigger: VNode, hint: string): VNode {
  return h(
    NPopover,
    { trigger: 'hover', placement: 'top', delay: 150 },
    {
      trigger: () => trigger,
      default: () => hint,
    }
  );
}

// 5. Computed properties and table columns
const columns: DataTableColumns<OrganisationInDb> = [
  {
    title: 'Name',
    key: 'name',
    sorter: true,
  },
  {
    title: 'Contract Review',
    key: 'contract_review',
    render(row): string {
      return row.features.includes(OrgFeature.CONTRACT_REVIEW) ? 'yes' : 'no';
    },
  },
  {
    title: 'Contract Management',
    key: 'contract_management',
    render(row): string {
      return row.features.includes(OrgFeature.CONTRACT_MANAGEMENT) ? 'yes' : 'no';
    },
  },
  {
    title: 'Gaia Signing',
    key: 'gaia_signing',
    render(row): string {
      return row.features.includes(OrgFeature.GAIA_SIGNING) ? 'yes' : 'no';
    },
  },
  {
    title: 'Contract Templating',
    key: 'contract_generation',
    render(row): string {
      return row.features.includes(OrgFeature.CONTRACT_GENERATION) ? 'yes' : 'no';
    },
  },
  {
    title: 'Credits',
    key: 'allowed_insights_credits',
    sorter: true,
    render(row): string {
      return row.allowed_insights_credits.toLocaleString() || '0';
    },
  },
  {
    title: 'Usage',
    key: 'usage',
    render(row): VNode {
      const usage = orgUsage.value[row.uid];
      const allowance = row.allowed_insights_credits || 0;

      let usageText: string;
      let usagePercent = 0;

      // If we have usage data (undefined = not yet fetched, null = fetch failed/unavailable)
      if (usage === null || usage === undefined) {
        usageText = 'N/A';
      } else {
        usageText = `${usage.toLocaleString()} / ${allowance.toLocaleString()}`;
        usagePercent = allowance > 0 ? Math.min(100, (usage / allowance) * 100) : 0;
      }

      // Use color based on usage percentage
      let color = '#18a058'; // Green for low usage
      if (usagePercent > 90) {
        color = '#d03050'; // Red for high usage (>90%)
      } else if (usagePercent > 70) {
        color = '#f0a020'; // Orange for medium-high usage (>70%)
      }

      return h('div', { class: 'usage-container' }, [
        h('div', { class: 'usage-text' }, usageText),
        usage === null || usage === undefined
          ? null
          : h('div', { class: 'usage-bar-container' }, [
              h('div', {
                class: 'usage-bar',
                style: {
                  width: `${usagePercent}%`,
                  backgroundColor: color,
                },
              }),
            ]),
      ]);
    },
  },
  {
    title: 'Billing Period',
    key: 'allowed_insights_credits_period',
    sorter: true,
    render(row): string {
      return row.allowed_insights_credits_period === BillingPeriod.MONTHLY ? 'Monthly' : 'Annual';
    },
  },
  {
    title: 'Billing Start Date',
    key: 'billing_start_date',
    sorter: true,
    render(row): string {
      if (!row.billing_start_date) return 'Not set';

      try {
        // Check if it's a valid date
        const date = new Date(row.billing_start_date);
        if (isNaN(date.getTime())) return 'Invalid date';

        // Format the date
        return date.toLocaleDateString();
      } catch {
        return 'Invalid date';
      }
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    fixed: 'right',
    width: 100,
    render(row): VNode {
      const menuIcon =
        (icon: object): (() => VNode) =>
        (): VNode =>
          h(NIcon, null, { default: () => h(icon) });

      // Every action except Switch collapses into a "⋯" menu.
      const moreOptions: DropdownOption[] = [
        { key: 'edit', label: 'Edit organization', icon: menuIcon(IconEdit) },
        { key: 'model-settings', label: 'Set AI models', icon: menuIcon(IconAgent) },
        { key: 'demo-data', label: 'Demo data', icon: menuIcon(IconExperiment) },
      ];
      if (!row.is_admin) {
        moreOptions.push({
          key: 'delete',
          label: 'Delete organization',
          icon: menuIcon(IconDelete),
        });
      }

      const onMoreSelect = (key: string | number): void => {
        switch (key) {
          case 'edit': {
            openEditModal(row);

            break;
          }
          case 'model-settings': {
            void openModelSettingsModal(row);

            break;
          }
          case 'demo-data': {
            openDemoDataModal(row);

            break;
          }
          case 'delete': {
            openDeleteModal(row);

            break;
          }
          // No default
        }
      };

      const moreButton = h(
        NDropdown,
        {
          trigger: 'click',
          placement: 'bottom-end',
          options: moreOptions,
          onSelect: onMoreSelect,
        },
        {
          default: () =>
            wrapWithActionPopover(
              h(
                NButton,
                { quaternary: true, size: 'small' },
                { icon: () => h(NIcon, null, { default: () => h(IconMore) }) }
              ),
              'More actions'
            ),
        }
      );

      const switchButton = h(
        NPopconfirm,
        {
          onPositiveClick: () => switchToOrganization(row.uid, row.name),
        },
        {
          default: () => `Switch to organization "${row.name}"?`,
          trigger: () =>
            wrapWithActionPopover(
              h(
                NButton,
                {
                  quaternary: true,
                  size: 'small',
                  type: 'primary',
                },
                { icon: () => h(NIcon, null, { default: () => h(IconSwap) }) }
              ),
              'Switch to this organization'
            ),
        }
      );

      return h(
        NSpace,
        { align: 'center', justify: 'center', size: [8, 0], inline: true, wrap: false },
        {
          default: () => [switchButton, moreButton],
        }
      );
    },
  },
];

// Add a computed property for default sorter
const defaultSorter = computed(() => {
  return {
    columnKey: sortKey.value,
    order: sortOrder.value === 'asc' ? 'ascend' : 'descend',
  };
});

// 6. Functions
function handlePageChange(page: number): void {
  currentPage.value = page;
  fetchOrganizations();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1; // Reset to first page when changing page size
  fetchOrganizations();
}

function handleSearch(): void {
  currentPage.value = 1; // Reset to first page when searching
  fetchOrganizations();
}

function handleSort(sorter: { columnKey: string; order: SortOrder }): void {
  // Set the sort key if provided
  if (sorter.columnKey) {
    sortKey.value = sorter.columnKey;
  }

  // Set the sort order based on order, defaulting to 'asc' if cleared
  if (sorter.order === false) {
    // When sorting is cleared, default to ascending
    sortOrder.value = 'asc';
  } else {
    sortOrder.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  }

  // Refresh the data
  fetchOrganizations();
}

function fetchOrganizations(): void {
  const query: SearchQuery = {
    text: searchQuery.value,
    limit: pageSize.value,
    offset: (currentPage.value - 1) * pageSize.value,
    sort_key: sortKey.value,
    sort_direction: sortOrder.value === 'asc' ? 'asc' : 'desc',
  };

  void store.fetchOrganizations(query).then(() => {
    if (store.error) message.error(store.error);
  });
}

// Function to fetch usage data for each organization
async function fetchAllOrganizationsUsage(): Promise<void> {
  if (store.organizations.length === 0) return;

  // Set loading state
  refreshingUsage.value = true;

  try {
    // Reset usage data
    orgUsage.value = {};

    // Fetch usage for each organization
    const fetchPromises = store.organizations.map(async org => {
      try {
        if (org.uid) {
          const usage = await store.fetchOrganizationAllowanceUsage(org.uid);
          // Store the usage as number or null, handle different response formats
          if (usage === null) {
            orgUsage.value[org.uid] = null;
          } else {
            // Make sure we convert to a number if it's a string
            orgUsage.value[org.uid] = typeof usage === 'string' ? parseFloat(usage) : usage;
          }
        }
      } catch (error) {
        logger.error(`Failed to fetch usage for organization ${org.name}:`, error);
        orgUsage.value[org.uid] = null;
      }
    });

    await Promise.allSettled(fetchPromises);
  } finally {
    // Clear loading state
    refreshingUsage.value = false;
  }
}

// Create functions
function openCreateModal(): void {
  // Reset form values
  createFormModel.name = '';
  createNameError.value = undefined;
  createFormModel.description = null;
  createFormModel.allowed_insights_credits = 100;
  createFormModel.allowed_insights_credits_period = 'monthly';
  createFormModel.billing_start_date = new Date().toISOString().slice(0, 10);
  Object.assign(createFormFeatureFlags, featureSwitches());
  createFormModelSettings.value = {};
  Object.assign(createFormMemberAccess, {
    all_contractual_parties_access: AccessPermission._0,
    all_contracts_access: AccessPermission._0,
  });
  showCreateModal.value = true;
}

function cancelCreateModal(): void {
  showCreateModal.value = false;
}

async function submitCreateForm(): Promise<void> {
  createFormModel.name = createFormModel.name.trim();
  if (!createFormModel.name) {
    createNameError.value = 'Please enter a name';
    return;
  }
  createNameError.value = undefined;

  const features = enabledFeatures(createFormFeatureFlags);
  const createData: OrganisationCreate = {
    name: createFormModel.name,
    description: createFormModel.description,
    features,
  };

  const result = await store.createOrganization(createData, createFormModelSettings.value, {
    ...createFormMemberAccess,
  });
  if (!result) {
    message.error(store.error ?? 'Failed to create organization');
    return;
  }

  await store.updateOrganization(
    result.uid,
    {
      allowed_insights_credits: createFormModel.allowed_insights_credits,
      allowed_insights_credits_period: isEnumValue(
        BillingPeriod,
        createFormModel.allowed_insights_credits_period
      )
        ? createFormModel.allowed_insights_credits_period
        : undefined,
      billing_start_date: createFormModel.billing_start_date,
    },
    // The features PUT replaces the whole set, so re-send what was created.
    features
  );

  // Refresh the usage data for all organizations
  await fetchAllOrganizationsUsage();

  message.success('Organization created successfully');
  showCreateModal.value = false;
}

// Edit functions
function openEditModal(org: OrganisationInDb): void {
  currentEditId.value = org.uid;
  // Populate form with current values
  editFormModel.name = org.name;
  editFormModel.description = org.description ?? null;
  editFormModel.allowed_insights_credits = org.allowed_insights_credits;
  editFormModel.allowed_insights_credits_period = org.allowed_insights_credits_period;

  Object.assign(updateFormFeatureFlags, featureSwitches(org.features));
  missingExtractions.value = null;
  if (org.features.includes(OrgFeature.CONTRACT_MANAGEMENT)) {
    void loadMissingExtractions(org.uid);
  }

  // Handle billing start date properly
  if (org.billing_start_date && org.billing_start_date !== 'null') {
    // Ensure proper date format
    try {
      // Check if it's a valid date
      const date = new Date(org.billing_start_date);
      if (isNaN(date.getTime())) {
        editFormModel.billing_start_date = null;
      } else {
        editFormModel.billing_start_date = date.toISOString().slice(0, 10);
      }
    } catch {
      logger.error('Invalid date:', org.billing_start_date);
      editFormModel.billing_start_date = null;
    }
  } else {
    editFormModel.billing_start_date = null;
  }

  showEditModal.value = true;
}

function cancelEditModal(): void {
  showEditModal.value = false;
  currentEditId.value = '';
}

function submitEditForm(): void {
  void editFormRef.value?.validate(errors => {
    void (async (): Promise<void> => {
      if (!errors) {
        const updateData: OrganisationUpdate = {
          name: editFormModel.name,
          description: editFormModel.description,
          allowed_insights_credits: editFormModel.allowed_insights_credits,
          allowed_insights_credits_period: isEnumValue(
            BillingPeriod,
            editFormModel.allowed_insights_credits_period
          )
            ? editFormModel.allowed_insights_credits_period
            : undefined,
          billing_start_date: editFormModel.billing_start_date,
        };

        const result = await store.updateOrganization(
          currentEditId.value,
          updateData,
          enabledFeatures(updateFormFeatureFlags)
        );

        if (result) {
          // Refresh the usage data for the updated organization
          if (currentEditId.value) {
            const usage = await store.fetchOrganizationAllowanceUsage(currentEditId.value);
            if (usage !== null) {
              orgUsage.value[currentEditId.value] = usage;
            }
          }

          message.success('Organization updated successfully');
          showEditModal.value = false;
          currentEditId.value = '';
        }
      }
    })();
  });
}

// Missing extractions (calendar / view-column back-fill)
async function loadMissingExtractions(orgUid: string): Promise<void> {
  const counts = await store.fetchMissingExtractions(orgUid);
  // Guard against the modal being closed or reopened for another org meanwhile.
  if (currentEditId.value === orgUid) {
    missingExtractions.value = counts;
  }
}

async function runMissingExtractions(): Promise<void> {
  const orgUid = currentEditId.value;
  runningExtractions.value = true;
  try {
    const counts = await store.runMissingExtractions(orgUid);
    message.success(`Started ${counts.total.toLocaleString()} missing extractions`);
    // Extractions run in the background, so this only drops as they land.
    await loadMissingExtractions(orgUid);
  } catch (error_) {
    message.error(error_ instanceof Error ? error_.message : 'Failed to start missing extractions');
  } finally {
    runningExtractions.value = false;
  }
}

// Model settings functions
function isModelSettingsOrgStillOpen(orgUid: string): boolean {
  return currentModelSettingsOrg.value?.uid === orgUid;
}

async function openModelSettingsModal(org: OrganisationInDb): Promise<void> {
  currentModelSettingsOrg.value = org;
  modelSettingsForm.value = {};
  showModelSettingsModal.value = true;

  modelSettingsLoading.value = true;
  try {
    const settings = await store.fetchOrganizationModelSettings(org.uid);
    // Guard against the modal being closed or reopened for another org meanwhile.
    // The function boundary keeps TS from narrowing past the mutation during await.
    if (settings && isModelSettingsOrgStillOpen(org.uid)) {
      modelSettingsForm.value = {
        simple_task_provider: settings.simple_task_provider,
        simple_task_model: settings.simple_task_model,
        complex_task_provider: settings.complex_task_provider,
        complex_task_model: settings.complex_task_model,
      };
    }
  } finally {
    modelSettingsLoading.value = false;
  }
}

function cancelModelSettingsModal(): void {
  showModelSettingsModal.value = false;
  currentModelSettingsOrg.value = null;
}

async function submitModelSettingsForm(): Promise<void> {
  if (!currentModelSettingsOrg.value) return;

  const success = await store.updateOrganizationModelSettings(
    currentModelSettingsOrg.value.uid,
    modelSettingsForm.value
  );

  if (success) {
    message.success('AI model settings updated successfully');
    showModelSettingsModal.value = false;
    currentModelSettingsOrg.value = null;
  }
}

// Delete functions
function openDeleteModal(org: OrganisationInDb): void {
  orgPendingDeletion.value = org;
  deleteConfirmationInput.value = '';
  showDeleteModal.value = true;
}

function cancelDeleteModal(): void {
  showDeleteModal.value = false;
  orgPendingDeletion.value = null;
  deleteConfirmationInput.value = '';
}

async function confirmDeleteOrganization(): Promise<void> {
  if (!orgPendingDeletion.value || !deleteNameMatches.value) return;

  const success = await store.deleteOrganization(orgPendingDeletion.value.uid);
  if (success) {
    message.success('Organization deleted successfully');
    cancelDeleteModal();
  }
}

// Demo data functions — kickoff only; progress arrives over the websocket and
// is shown by the global bottom-right DemoSeedPanel.
function openDemoDataModal(org: OrganisationInDb): void {
  demoDataOrg.value = org;
  selectedDemoAreas.value = DEMO_AREA_OPTIONS.map(option => option.value);
  seededDemoAreas.value = {};
  showDemoDataModal.value = true;
  void fetchSeededDemoAreas(org.uid);
}

async function fetchSeededDemoAreas(organisationUid: string): Promise<void> {
  const response = await getDemoDataStatus({ query: { organisation_uid: organisationUid } });
  if (response.data && demoDataOrg.value?.uid === organisationUid) {
    seededDemoAreas.value = response.data.areas;
  }
}

async function submitSeedDemoData(): Promise<void> {
  if (!demoDataOrg.value || selectedDemoAreas.value.length === 0) return;

  seedDemoLoading.value = true;
  try {
    await demoSeedStore.startSeeding(selectedDemoAreas.value, demoDataOrg.value.uid);
    showDemoDataModal.value = false;
  } catch (error) {
    message.error(
      error instanceof Error && error.message ? error.message : 'Failed to start demo data seeding'
    );
    logger.error('Error starting demo data seeding:', error);
  } finally {
    seedDemoLoading.value = false;
  }
}

async function submitRemoveDemoData(): Promise<void> {
  if (!demoDataOrg.value) return;

  removeDemoLoading.value = true;
  try {
    await demoSeedStore.startRemoval(demoDataOrg.value.uid);
    showDemoDataModal.value = false;
  } catch (error) {
    message.error(
      error instanceof Error && error.message ? error.message : 'Failed to start demo data removal'
    );
    logger.error('Error starting demo data removal:', error);
  } finally {
    removeDemoLoading.value = false;
  }
}

async function switchToOrganization(uid: string, name: string): Promise<void> {
  try {
    const result = await orgStore.switchToOrganization(uid);
    if (result) {
      message.success(`Switched to organization "${name}"`);
      window.location.href = '/';
    }
  } catch (error) {
    message.error('Failed to switch organization');
    logger.error('Error switching organization:', error);
  }
}

// 7. Lifecycle hooks
onMounted(async (): Promise<void> => {
  fetchOrganizations();
  await fetchAllOrganizationsUsage();
});

// Ensure we update when organizations change
watch(
  () => store.organizations,
  async (): Promise<void> => {
    if (store.organizations.length > 0) {
      await fetchAllOrganizationsUsage();
    }
  },
  { deep: true }
);
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.adminArea }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.adminOrganizations }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        Organizations Management
      </gaia-header>

      <!-- Search and Action Bar -->
      <NSpace justify="space-between" align="center" style="margin-bottom: 16px">
        <!-- Search Input (smaller width) -->
        <gaia-search-input
          v-model:value="searchQuery"
          placeholder="Search organizations"
          @search="handleSearch"
        />

        <!-- Action Buttons -->
        <NSpace>
          <NButton :loading="refreshingUsage" @click="fetchAllOrganizationsUsage">
            <template #icon>
              <NIcon>
                <IconRefresh />
              </NIcon>
            </template>
            Refresh Usage
          </NButton>
          <NButton type="primary" @click="openCreateModal">
            <template #icon>
              <NIcon>
                <IconAdd />
              </NIcon>
            </template>
            New Organization
          </NButton>
        </NSpace>
      </NSpace>
    </n-flex>

    <NAlert v-if="store.error" type="error" :title="store.error" class="error-alert" />

    <div v-if="!store.loading && !store.hasOrganizations" class="empty-state">
      <NEmpty description="No organizations found">
        <template #extra>
          <NButton type="primary" @click="openCreateModal"> Create New Organization </NButton>
        </template>
      </NEmpty>
    </div>

    <gaia-table
      v-else
      :columns="columns"
      :data="store.organizations"
      :loading="store.loading"
      :pagination="false"
      striped
      :default-sort-order="defaultSorter"
      @update:sorter="handleSort"
    />

    <div v-if="store.hasOrganizations" class="pagination-container">
      <gaia-pagination
        :page="currentPage"
        :page-size="pageSize"
        :total="store.total"
        :page-sizes="[10, 20, 50]"
        @update:page="handlePageChange"
        @update:page-size="handlePageSizeChange"
      />
    </div>

    <!-- Create Organization Modal -->
    <gaia-modal
      v-model:show="showCreateModal"
      width="600px"
      :mask-closable="false"
      :closable="!store.loading"
    >
      <template #header>
        <n-flex vertical :size="4">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Create organization</gaia-text>
          <gaia-text size="control" tone="subtle">
            Set up a new workspace with its plan, features and access defaults.
          </gaia-text>
        </n-flex>
      </template>

      <n-flex vertical :size="28">
        <n-flex vertical :size="14">
          <gaia-section-header variant="rule">Details</gaia-section-header>
          <gaia-form-field label="Name" required :error="createNameError">
            <gaia-input
              v-model:value="createFormModel.name"
              size="large"
              placeholder="Lindqvist & Partners"
              :status="createNameError ? 'error' : undefined"
              @update:value="createNameError = undefined"
            />
          </gaia-form-field>
          <gaia-form-field label="Description" hint="Optional">
            <gaia-textarea
              v-model:value="createFormModel.description"
              placeholder="What this organization is for"
              :autosize="{ minRows: 2, maxRows: 4 }"
            />
          </gaia-form-field>
        </n-flex>

        <n-flex vertical :size="14">
          <gaia-section-header variant="rule">Plan</gaia-section-header>
          <n-grid :cols="3" :x-gap="12">
            <n-gi>
              <gaia-form-field label="Insights credits">
                <gaia-input-number
                  v-model:value="createFormModel.allowed_insights_credits"
                  :min="0"
                  :precision="0"
                  style="width: 100%"
                />
              </gaia-form-field>
            </n-gi>
            <n-gi>
              <gaia-form-field label="Billing period">
                <gaia-select
                  v-model:value="createFormModel.allowed_insights_credits_period"
                  :options="CREDITS_PERIOD_OPTIONS"
                  style="width: 100%"
                />
              </gaia-form-field>
            </n-gi>
            <n-gi>
              <gaia-form-field label="Billing start">
                <gaia-date-picker
                  v-model:formatted-value="createFormModel.billing_start_date"
                  value-format="yyyy-MM-dd"
                  type="date"
                  style="width: 100%"
                />
              </gaia-form-field>
            </n-gi>
          </n-grid>
        </n-flex>

        <n-flex vertical :size="14">
          <gaia-section-header variant="rule">Features</gaia-section-header>
          <n-grid :cols="2" :x-gap="10" :y-gap="10">
            <n-gi>
              <gaia-toggle-row
                label="Contract review"
                hint="Reviews, playbooks and review projects"
                :checked="createFormFeatureFlags.contract_review"
                @update:checked="
                  (value: boolean) => (createFormFeatureFlags.contract_review = value)
                "
              />
            </n-gi>
            <n-gi>
              <gaia-toggle-row
                label="Contract management"
                hint="Contract repository and extractions"
                :checked="createFormFeatureFlags.contract_management"
                @update:checked="
                  (value: boolean) => (createFormFeatureFlags.contract_management = value)
                "
              />
            </n-gi>
            <n-gi>
              <gaia-toggle-row
                label="Signing with Gaia"
                hint="Send documents for e-signature"
                :checked="createFormFeatureFlags.gaia_signing"
                @update:checked="(value: boolean) => (createFormFeatureFlags.gaia_signing = value)"
              />
            </n-gi>
            <n-gi>
              <gaia-toggle-row
                label="Contract templating"
                hint="Templates and generated drafts"
                :checked="createFormFeatureFlags.contract_generation"
                @update:checked="
                  (value: boolean) => (createFormFeatureFlags.contract_generation = value)
                "
              />
            </n-gi>
          </n-grid>
        </n-flex>

        <n-flex vertical :size="14">
          <gaia-section-header variant="rule">Member access</gaia-section-header>
          <n-flex vertical :size="10">
            <gaia-select-row
              label="Access to all contractual parties"
              hint="On top of the parties shared with each member individually"
              :value="createFormMemberAccess.all_contractual_parties_access"
              :options="ORG_WIDE_ACCESS_OPTIONS"
              @update:value="
                (value: string | number) =>
                  (createFormMemberAccess.all_contractual_parties_access =
                    value as AccessPermission)
              "
            />
            <gaia-select-row
              label="Access to all contracts"
              hint="On top of the contracts shared with each member individually"
              :value="createFormMemberAccess.all_contracts_access"
              :options="ORG_WIDE_ACCESS_OPTIONS"
              @update:value="
                (value: string | number) =>
                  (createFormMemberAccess.all_contracts_access = value as AccessPermission)
              "
            />
          </n-flex>
        </n-flex>

        <n-flex vertical :size="10">
          <gaia-section-header variant="rule">AI models</gaia-section-header>
          <gaia-text size="control" tone="subtle">
            Leave empty to use the platform defaults.
          </gaia-text>
          <OrgModelSettingsFormItems v-model="createFormModelSettings" />
        </n-flex>
      </n-flex>

      <template #action>
        <n-flex justify="flex-end" :size="8">
          <gaia-button size="small" :disabled="store.loading" @click="cancelCreateModal">
            Cancel
          </gaia-button>
          <gaia-create-button
            subject="organization"
            size="sm"
            :icon="false"
            :loading="store.loading"
            @click="submitCreateForm"
          />
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Demo Data Modal (seed + remove, targets the clicked row's organisation) -->
    <n-modal v-model:show="showDemoDataModal" preset="card" title="Demo Data" style="width: 500px">
      <n-flex vertical :size="16">
        <n-alert type="info">
          Seeding and removal apply to
          <strong>{{ demoDataOrg?.name }}</strong
          >. Re-seeding an area skips anything that already exists; removal deletes exactly the
          entities seeding creates.
        </n-alert>
        <n-flex vertical :size="8">
          <n-text depth="3">Areas to seed (removal always clears all areas)</n-text>
          <gaia-checkbox-group v-model:value="selectedDemoAreas">
            <n-flex vertical :size="8">
              <gaia-checkbox
                v-for="option in DEMO_AREA_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                <n-flex align="center" :size="8" inline>
                  {{ option.label }}
                  <n-tag v-if="seededDemoAreas[option.value]" size="small" type="success" round>
                    Seeded
                  </n-tag>
                </n-flex>
              </gaia-checkbox>
            </n-flex>
          </gaia-checkbox-group>
        </n-flex>
      </n-flex>

      <template #footer>
        <n-flex justify="space-between" align="center">
          <n-popconfirm @positive-click="submitRemoveDemoData">
            <template #trigger>
              <n-button
                type="error"
                quaternary
                :disabled="demoSeedStore.isRunning"
                :loading="removeDemoLoading"
              >
                Remove All Demo Data
              </n-button>
            </template>
            Remove all demo data from {{ demoDataOrg?.name }}?
          </n-popconfirm>
          <n-button
            type="primary"
            :disabled="selectedDemoAreas.length === 0 || demoSeedStore.isRunning"
            :loading="seedDemoLoading"
            @click="submitSeedDemoData"
          >
            {{ demoSeedStore.isRunning ? 'Run in progress…' : 'Seed' }}
          </n-button>
        </n-flex>
      </template>
    </n-modal>

    <!-- Delete Organization Modal -->
    <NModal
      v-model:show="showDeleteModal"
      preset="card"
      title="Delete Organization"
      style="width: 640px"
      :mask-closable="false"
    >
      <NSpace vertical :size="16">
        <NAlert type="error" title="This action cannot be undone">
          You are about to permanently delete
          <strong>{{ orgPendingDeletion?.name }}</strong> and all of its data.
        </NAlert>
        <NText>
          Type <NText strong code>{{ orgPendingDeletion?.name }}</NText> below to confirm.
        </NText>
        <gaia-input
          v-model:value="deleteConfirmationInput"
          size="large"
          :placeholder="orgPendingDeletion?.name"
          @keydown.enter="confirmDeleteOrganization"
        />
      </NSpace>

      <template #footer>
        <div class="modal-footer">
          <NButton size="large" @click="cancelDeleteModal">Cancel</NButton>
          <NButton
            type="error"
            size="large"
            :disabled="!deleteNameMatches"
            :loading="store.loading"
            @click="confirmDeleteOrganization"
          >
            Delete Organization
          </NButton>
        </div>
      </template>
    </NModal>

    <!-- AI Model Settings Modal -->
    <NModal
      v-model:show="showModelSettingsModal"
      preset="card"
      :title="`AI Models — ${currentModelSettingsOrg?.name ?? ''}`"
      style="width: 600px"
      :mask-closable="false"
    >
      <NText depth="3" style="display: block; margin-bottom: 16px">
        Leave a field empty to keep the organization's current setting.
      </NText>
      <NSpin :show="modelSettingsLoading">
        <NForm label-placement="top">
          <OrgModelSettingsFormItems v-model="modelSettingsForm" />
        </NForm>
      </NSpin>

      <template #footer>
        <div class="modal-footer">
          <NButton @click="cancelModelSettingsModal">Cancel</NButton>
          <NButton
            type="primary"
            :disabled="modelSettingsLoading"
            :loading="store.loading"
            @click="submitModelSettingsForm"
          >
            Save
          </NButton>
        </div>
      </template>
    </NModal>

    <!-- Edit Organization Modal -->
    <NModal
      v-model:show="showEditModal"
      preset="card"
      title="Edit Organization"
      style="width: 500px"
      content-style="max-height: 70vh; overflow-y: auto"
      :mask-closable="false"
    >
      <NForm
        ref="editFormRef"
        :model="editFormModel"
        :rules="formRules"
        label-placement="left"
        label-align="left"
        label-width="160"
        require-mark-placement="right-hanging"
      >
        <NFormItem label="Name" path="name">
          <gaia-input v-model:value="editFormModel.name" placeholder="Enter organization name" />
        </NFormItem>

        <NFormItem label="Description" path="description">
          <gaia-textarea
            v-model:value="editFormModel.description"
            placeholder="Enter organization description"
            :autosize="{ minRows: 3, maxRows: 5 }"
          />
        </NFormItem>

        <NFormItem label="Insights Credits" path="allowed_insights_credits">
          <gaia-input-number
            v-model:value="editFormModel.allowed_insights_credits"
            :min="0"
            :precision="0"
            placeholder="Enter allowed credits"
          />
        </NFormItem>

        <NFormItem label="Billing Period" path="allowed_insights_credits_period">
          <gaia-select
            v-model:value="editFormModel.allowed_insights_credits_period"
            :options="CREDITS_PERIOD_OPTIONS"
          />
        </NFormItem>

        <NFormItem label="Billing Start Date" path="billing_start_date">
          <gaia-date-picker
            v-model:formatted-value="editFormModel.billing_start_date"
            value-format="yyyy-MM-dd"
            type="date"
            clearable
          />
        </NFormItem>

        <NFormItem label="Contract Reviews" path="contract_reviews">
          <gaia-switch v-model:value="updateFormFeatureFlags.contract_review">
            <template #checked>Enabled</template>
            <template #unchecked>Disabled</template>
          </gaia-switch>
        </NFormItem>

        <NFormItem label="Contract Management" path="contract_management">
          <gaia-switch v-model:value="updateFormFeatureFlags.contract_management">
            <template #checked>Enabled</template>
            <template #unchecked>Disabled</template>
          </gaia-switch>
        </NFormItem>

        <!-- Reflects the saved state (not the switch above), so an unsaved toggle never offers a run. -->
        <NFormItem
          v-if="missingExtractions?.contract_management_enabled"
          label="Missing Extractions"
          :show-feedback="false"
        >
          <n-flex align="center" justify="space-between" :size="12" :wrap="false" style="flex: 1">
            <gaia-text tone="muted" size="dense">
              {{ missingExtractions.total.toLocaleString() }} ({{
                missingExtractions.calendars.toLocaleString()
              }}
              calendar, {{ missingExtractions.view_columns.toLocaleString() }} view column)
            </gaia-text>
            <gaia-popconfirm
              :title="`Run ${missingExtractions.total.toLocaleString()} extractions?`"
              confirm-label="Run"
              @confirm="runMissingExtractions"
            >
              <template #trigger>
                <gaia-button
                  size="small"
                  :disabled="missingExtractions.total === 0"
                  :loading="runningExtractions"
                >
                  Run
                </gaia-button>
              </template>
              Uses {{ missingExtractions.total.toLocaleString() }} insights credits ({{
                missingExtractions.remaining_insights_credits.toLocaleString()
              }}
              left). Runs in the background.
            </gaia-popconfirm>
          </n-flex>
        </NFormItem>

        <NFormItem label="Signing with Gaia" path="gaia_signing">
          <gaia-switch v-model:value="updateFormFeatureFlags.gaia_signing">
            <template #checked>Enabled</template>
            <template #unchecked>Disabled</template>
          </gaia-switch>
        </NFormItem>

        <NFormItem label="Contract Templating" path="contract_generation">
          <gaia-switch v-model:value="updateFormFeatureFlags.contract_generation">
            <template #checked>Enabled</template>
            <template #unchecked>Disabled</template>
          </gaia-switch>
        </NFormItem>
      </NForm>

      <template #footer>
        <div class="modal-footer">
          <NButton @click="cancelEditModal">Cancel</NButton>
          <NButton type="primary" :loading="store.loading" @click="submitEditForm">
            Save Changes
          </NButton>
        </div>
      </template>
    </NModal>
  </Layout>
</template>

<style scoped>
.error-alert {
  margin-bottom: 16px;
}

.empty-state {
  padding: 30px 0;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.n-input .n-input__input) {
  padding-right: 30px; /* Leave space for the clear button */
}

:deep(.n-data-table-th) {
  font-weight: 600;
}

:deep(.n-data-table-td) {
  padding: 12px 14px;
}

:deep(.n-button) {
  transition: all 0.2s ease;
}

.usage-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.usage-text {
  font-size: 0.9em;
}

.usage-bar-container {
  width: 100%;
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.usage-bar {
  height: 100%;
  border-radius: 3px;
  transition:
    width 0.3s ease,
    background-color 0.3s ease;
}
</style>
